import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  Search,
  Plus,
  MoreVertical,
  Eye,
  UserCog,
  UserX,
  UserCheck,
  LogIn,
  RefreshCw,
  Mail,
  Phone,
  Briefcase,
  Shield,
  Calendar,
  DollarSign,
  ArrowLeft,
  ExternalLink,
  AlertTriangle,
  FileText,
  Pencil,
  Sliders
} from 'lucide-react';
import { toast } from 'sonner';
import EditInvestorDialog from '../../components/admin/EditInvestorDialog';
import { TrafficDot } from '../../components/admin/TrafficLights';
import ActiveSwitch from '../../components/admin/ActiveSwitch';
import axios from 'axios';
import { SortableHeader, useSort } from '../../components/ui/sortable-header';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const KycStatusBadge = ({ status }) => {
  const config = {
    approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
    pending: { color: 'bg-amber-100 text-amber-800', label: 'Pending' },
    incomplete: { color: 'bg-red-100 text-red-800', label: 'Incomplete' },
    not_started: { color: 'bg-gray-100 text-gray-800', label: 'Not Started' }
  };
  const { color, label } = config[status] || config.not_started;
  return <Badge className={color}>{label}</Badge>;
};

export default function AdminInvestorsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [investorTypeFilter, setInvestorTypeFilter] = useState('all');
  const [investorTypes, setInvestorTypes] = useState([]);
  const [welcomeTemplates, setWelcomeTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [bulkTemplateId, setBulkTemplateId] = useState('auto');
  const [bulkSending, setBulkSending] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [trafficMap, setTrafficMap] = useState({});
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkTypeCode, setBulkTypeCode] = useState('__none__');
  const [bulkActiveState, setBulkActiveState] = useState('__none__');
  const [bulkMandateType, setBulkMandateType] = useState('__none__');
  const [bulkEntityType, setBulkEntityType] = useState('__none__');
  
  // Dialog states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [investorDetail, setInvestorDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: ''
  });
  const [inviting, setInviting] = useState(false);
  const { sortConfig, handleSort, sortItems } = useSort('first_name', 'asc');

  const fetchInvestors = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (investorTypeFilter !== 'all') params.append('investor_type', investorTypeFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/investors?${params}`, { headers });
      setInvestors(response.data.items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
    } catch (error) {
      console.error('Error fetching investors:', error);
      toast.error('Failed to load investors');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.limit, search, statusFilter, investorTypeFilter]);

  // Load investor types & welcome templates once for the filter + bulk modal.
  useEffect(() => {
    const load = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [tp, tpl, tl] = await Promise.all([
          axios.get(`${API_URL}/api/admin/investor-types`, { headers }),
          axios.get(`${API_URL}/api/admin/templates`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/admin/investors/traffic-lights`, { headers }).catch(() => ({ data: { lights: {} } })),
        ]);
        setInvestorTypes(tp.data.types || []);
        // Templates endpoint returns { templates: [...] } — filter to welcome_* variants.
        const rows = Array.isArray(tpl.data) ? tpl.data : (tpl.data.templates || []);
        setWelcomeTemplates(rows.filter(t => (t.template_type || '').startsWith('welcome_') && t.is_active !== false));
        setTrafficMap(tl.data.lights || {});
      } catch (e) {
        // non-fatal — filter just won't populate.
      }
    };
    if (token) load();
  }, [token]);

  useEffect(() => {
    fetchInvestors();
  }, [fetchInvestors]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchInvestors();
  };

  const handleViewDetail = async (investor) => {
    setSelectedInvestor(investor);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/investors/${investor.id}`, { headers });
      setInvestorDetail(response.data);
    } catch (error) {
      console.error('Error fetching investor detail:', error);
      toast.error('Failed to load investor details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setInviting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/investors/invite`, inviteForm, { headers });
      toast.success('Invitation sent successfully');
      setShowInviteDialog(false);
      setInviteForm({ email: '', first_name: '', last_name: '' });
    } catch (error) {
      console.error('Error inviting investor:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleDeactivate = async (investor) => {
    if (!window.confirm(`Disable portal login for ${investor.first_name} ${investor.last_name}? (This only blocks their login — use the Book switch to make them inactive for new allocations.)`)) {
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/investors/${investor.id}/deactivate`, {}, { headers });
      toast.success('Investor account deactivated');
      fetchInvestors();
    } catch (error) {
      console.error('Error deactivating investor:', error);
      toast.error('Failed to deactivate account');
    }
  };

  const handleActivate = async (investor) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/investors/${investor.id}/activate`, {}, { headers });
      toast.success('Investor account activated');
      fetchInvestors();
    } catch (error) {
      console.error('Error activating investor:', error);
      toast.error('Failed to activate account');
    }
  };

  const handleImpersonate = async (investor) => {
    setSelectedInvestor(investor);
    setShowImpersonateDialog(true);
  };

  // Send Portal Invite — reuses the /admin/bulk/send-welcome-emails
  // endpoint. Generates a one-time password-set token, stores it in
  // password_resets (24h expiry), and emails the "Welcome — Set Your
  // Password" template. The email includes the /reset-password?token=…
  // URL as selectable text so the recipient can copy-paste (no
  // clickable links per PTC's design).
  const handleSendInvite = async (investor) => {
    if (investor.welcome_email_sent && !window.confirm(
      `${investor.first_name} ${investor.last_name} has already been invited once. ` +
      `Sending again will generate a NEW password-set token and invalidate the ` +
      `previous one. Proceed?`
    )) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const r = await axios.post(
        `${API_URL}/api/admin/bulk/send-welcome-emails`,
        { user_ids: [investor.id] },
        { headers }
      );
      const sent = r.data.sent_count || 0;
      const failed = r.data.failed_count || 0;
      if (sent > 0) {
        toast.success(`Invite sent to ${investor.email}`);
        fetchInvestors();
      } else {
        const err = (r.data.errors && r.data.errors[0]?.error) || r.data.message || 'Failed';
        toast.error(`Invite not sent: ${err}`);
      }
      // Surface any global config problem (e.g. email service not
      // configured) as a distinct warning rather than a silent success.
      if (failed === 0 && sent === 0) {
        toast.warning(r.data.message || 'Email service not configured');
      }
    } catch (error) {
      console.error('Invite failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleBulkSendInvites = () => {
    // Two modes: (a) admin has ticked specific rows → use those,
    //            (b) nothing selected → default to all visible uninvited.
    if (selectedIds.size === 0) {
      const uninvited = investors.filter(i => i.is_active !== false && !i.welcome_email_sent);
      if (uninvited.length === 0) {
        toast.info('Every visible investor has already been invited. Tick specific rows to re-send.');
        return;
      }
      setSelectedIds(new Set(uninvited.map(i => i.id)));
    }
    setBulkTemplateId('auto');
    setBulkInviteOpen(true);
  };

  const submitBulkSendInvites = async () => {
    if (selectedIds.size === 0) { setBulkInviteOpen(false); return; }
    setBulkSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const body = { user_ids: Array.from(selectedIds) };
      if (bulkTemplateId && bulkTemplateId !== 'auto') body.template_id = bulkTemplateId;
      const r = await axios.post(`${API_URL}/api/admin/bulk/send-welcome-emails`, body, { headers });
      const sent = r.data.sent_count || 0;
      const failed = r.data.failed_count || 0;
      if (sent > 0) toast.success(`${sent} invite(s) sent successfully`);
      if (failed > 0) toast.warning(`${failed} invite(s) failed — check the errors below.`);
      if (sent === 0 && failed === 0) toast.warning(r.data.message || 'Email service not configured');
      setBulkInviteOpen(false);
      setSelectedIds(new Set());
      fetchInvestors();
    } catch (error) {
      console.error('Bulk invite failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk invite failed');
    } finally {
      setBulkSending(false);
    }
  };

  const submitBulkEdit = async () => {
    if (selectedIds.size === 0) return;
    const payload = { user_ids: Array.from(selectedIds) };
    if (bulkTypeCode !== '__none__') payload.investor_type = bulkTypeCode;
    if (bulkActiveState !== '__none__') payload.is_active = bulkActiveState === 'active';
    if (bulkMandateType !== '__none__') payload.mandate_type = bulkMandateType;
    if (bulkEntityType !== '__none__') payload.entity_type = bulkEntityType;
    if (!payload.investor_type && payload.is_active === undefined && !payload.mandate_type && !payload.entity_type) {
      toast.error('Pick at least one field to change');
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/admin/users/bulk-update`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message || 'Updated');
      setBulkEditOpen(false);
      setSelectedIds(new Set());
      fetchInvestors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk edit failed');
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === investors.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(investors.map(i => i.id)));
  };

  const confirmImpersonate = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_URL}/api/admin/impersonate/${selectedInvestor.id}`, {}, { headers });
      
      // Store impersonation token and open in new tab
      const impersonationUrl = `${window.location.origin}/dashboard?impersonate=${response.data.access_token}`;
      window.open(impersonationUrl, '_blank');
      
      toast.success(`Impersonating ${selectedInvestor.first_name} ${selectedInvestor.last_name}. New tab opened.`);
      setShowImpersonateDialog(false);
    } catch (error) {
      console.error('Error impersonating:', error);
      toast.error('Failed to impersonate investor');
    }
  };

  if (loading && investors.length === 0) {
    return (
      <div className="space-y-6" data-testid="admin-investors-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-investors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6" />
            Investor Management
          </h1>
          <p className="text-gray-500 mt-1">
            {pagination.total} total investors
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchInvestors}
            data-testid="refresh-investors-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleBulkSendInvites}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="bulk-send-invites-btn"
            title="Send a Welcome — Set Your Password email to selected investors (or all uninvited if none ticked)."
          >
            <Mail className="h-4 w-4 mr-2" />
            {selectedIds.size > 0 ? `Send Invites (${selectedIds.size})` : 'Send Invites (Bulk)'}
          </Button>
          {selectedIds.size > 0 && (
            <Button
              variant="outline"
              onClick={() => { setBulkTypeCode('__none__'); setBulkActiveState('__none__'); setBulkMandateType('__none__'); setBulkEntityType('__none__'); setBulkEditOpen(true); }}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="bulk-edit-btn"
              title="Change investor type or active status for the selected rows in one shot."
            >
              <Sliders className="h-4 w-4 mr-2" />
              Bulk Edit ({selectedIds.size})
            </Button>
          )}
          <Button
            onClick={() => setShowInviteDialog(true)}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="invite-investor-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Invite Investor
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="max-w-md"
                data-testid="search-investors-input"
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={investorTypeFilter} onValueChange={(v) => { setInvestorTypeFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="w-44" data-testid="filter-investor-type">
                <SelectValue placeholder="Investor type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {investorTypes.map(t => (
                  <SelectItem key={t.id} value={t.code}>{t.code} — {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Investors Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={investors.length > 0 && selectedIds.size === investors.length}
                    onChange={toggleSelectAll}
                    data-testid="select-all-investors"
                    aria-label="Select all"
                  />
                </TableHead>
                <SortableHeader label="Investor" sortKey="first_name" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Investor Code" sortKey="risk_participant_code" sortConfig={sortConfig} onSort={handleSort} />
                <TableHead>Type</TableHead>
                <SortableHeader label="Portfolio" sortKey="portfolio_value" sortConfig={sortConfig} onSort={handleSort} />
                <TableHead className="text-center w-16" title="KYC / FICA status">KYC</TableHead>
                <TableHead className="text-center w-20" title="Open queries / support tickets">Queries</TableHead>
                <SortableHeader label="Book" sortKey="portfolio_status" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Login" sortKey="is_active" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Last Login" sortKey="last_login" sortConfig={sortConfig} onSort={handleSort} />
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortItems(investors).map((investor) => (
                <TableRow key={investor.id} className="hover:bg-gray-50">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(investor.id)}
                      onChange={() => toggleSelected(investor.id)}
                      data-testid={`select-investor-${investor.id}`}
                      aria-label={`Select ${investor.first_name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-slate-600">
                          {investor.first_name?.[0]}{investor.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {investor.first_name} {investor.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{investor.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm text-gray-700" data-testid={`investor-code-${investor.id}`}>
                      {investor.risk_participant_code || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {(investor.investor_type || investor.brand) ? (
                      <Badge variant="outline" className="text-xs" data-testid={`investor-type-${investor.id}`}>
                        {investor.investor_type || investor.brand}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{formatCurrency(investor.portfolio_value || 0)}</p>
                      <p className="text-sm text-gray-500">{investor.active_loans || 0} active loans</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <TrafficDot
                      light={trafficMap[investor.id]?.kyc}
                      label="KYC"
                      testId={`traffic-kyc-${investor.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <TrafficDot
                      light={trafficMap[investor.id]?.unresolved_queries}
                      label="Open queries"
                      testId={`traffic-queries-${investor.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {/* Active / Inactive in the book: inactive = left, no new allocations; history kept */}
                    <ActiveSwitch kind="investor" id={investor.id} active={investor.portfolio_status !== 'inactive'}
                      label={investor.risk_participant_code || `${investor.first_name || ''} ${investor.last_name || ''}`.trim()}
                      onChanged={() => fetchInvestors()} testId={`investor-active-switch-${investor.id}`} />
                  </TableCell>
                  <TableCell>
                    <Badge className={investor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {investor.is_active ? 'Login on' : 'Login off'}
                    </Badge>
                    {(() => {
                      // Invite delivery state — differentiates "we never
                      // sent it" from "we sent it and it bounced" from
                      // "sent successfully".
                      const status = investor.welcome_email_last_status;
                      const ts = investor.welcome_email_last_sent_at;
                      const err = investor.welcome_email_last_error;
                      // Human-readable "N days ago" — cheap client-side format.
                      const ago = (iso) => {
                        if (!iso) return '';
                        const then = new Date(iso).getTime();
                        const diff = Date.now() - then;
                        if (isNaN(diff)) return '';
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        const days = Math.floor(hrs / 24);
                        if (days < 30) return `${days}d ago`;
                        return new Date(iso).toLocaleDateString();
                      };
                      if (status === 'sent') {
                        return (
                          <Badge
                            variant="outline"
                            className="ml-1 text-xs border-emerald-300 text-emerald-700"
                            title={`Invite email delivered ${ts ? ago(ts) : ''}`}
                            data-testid={`invite-status-sent-${investor.id}`}
                          >
                            Sent {ts ? ago(ts) : ''}
                          </Badge>
                        );
                      }
                      if (status === 'failed') {
                        return (
                          <Badge
                            variant="outline"
                            className="ml-1 text-xs border-red-300 text-red-700"
                            title={`Last attempt failed ${ts ? ago(ts) : ''}: ${err || 'unknown error'}`}
                            data-testid={`invite-status-failed-${investor.id}`}
                          >
                            Failed {ts ? ago(ts) : ''}
                          </Badge>
                        );
                      }
                      // Legacy fallback — invite pre-dates the status
                      // tracking (welcome_email_sent flag set but no
                      // per-attempt log).
                      if (investor.welcome_email_sent) {
                        return (
                          <Badge variant="outline" className="ml-1 text-xs border-emerald-300 text-emerald-700"
                                 title="Portal invite email already sent">
                            Invited
                          </Badge>
                        );
                      }
                      return (
                        <Badge variant="outline" className="ml-1 text-xs border-amber-300 text-amber-700"
                               title="No portal invite email sent yet"
                               data-testid={`invite-status-none-${investor.id}`}>
                          Not invited
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {formatDate(investor.last_login)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`investor-menu-${investor.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetail(investor)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditingInvestor(investor)} data-testid={`edit-investor-${investor.id}`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleImpersonate(investor)}>
                          <LogIn className="h-4 w-4 mr-2" />
                          Impersonate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleSendInvite(investor)}
                          data-testid={`send-invite-${investor.id}`}
                          className="text-emerald-700"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {investor.welcome_email_sent ? 'Re-send Portal Invite' : 'Send Portal Invite'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {investor.is_active ? (
                          <DropdownMenuItem onClick={() => handleDeactivate(investor)} className="text-red-600">
                            <UserX className="h-4 w-4 mr-2" />
                            Disable portal login
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleActivate(investor)} className="text-green-600">
                            <UserCheck className="h-4 w-4 mr-2" />
                            Enable portal login
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {investors.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>No investors found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            disabled={pagination.page === pagination.pages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Edit Investor Dialog */}
      <EditInvestorDialog
        open={!!editingInvestor}
        onOpenChange={(v) => !v && setEditingInvestor(null)}
        investor={editingInvestor}
        investorTypes={investorTypes}
        onSaved={() => { setEditingInvestor(null); fetchInvestors(); }}
      />

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk edit {selectedIds.size} investor(s)</DialogTitle>
            <DialogDescription>Leave a field on <em>Unchanged</em> to preserve current values for every row.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Set investor type</Label>
              <Select value={bulkTypeCode} onValueChange={setBulkTypeCode}>
                <SelectTrigger data-testid="bulk-edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unchanged —</SelectItem>
                  {investorTypes.map(t => <SelectItem key={t.id} value={t.code}>{t.code} · {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Set active status</Label>
              <Select value={bulkActiveState} onValueChange={setBulkActiveState}>
                <SelectTrigger data-testid="bulk-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unchanged —</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Set mandate type</Label>
              <Select value={bulkMandateType} onValueChange={setBulkMandateType}>
                <SelectTrigger data-testid="bulk-edit-mandate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unchanged —</SelectItem>
                  <SelectItem value="discretionary">Discretionary</SelectItem>
                  <SelectItem value="non_discretionary">Non-discretionary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Set entity type</Label>
              <Select value={bulkEntityType} onValueChange={setBulkEntityType}>
                <SelectTrigger data-testid="bulk-edit-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unchanged —</SelectItem>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Trust">Trust</SelectItem>
                  <SelectItem value="Company">Company (Pty) Ltd</SelectItem>
                  <SelectItem value="CC">Close Corp (CC)</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="Retirement Fund">Retirement Fund</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
            <Button onClick={submitBulkEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-edit-confirm">Apply to {selectedIds.size}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Invites Dialog */}
      <Dialog open={bulkInviteOpen} onOpenChange={setBulkInviteOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send welcome invites</DialogTitle>
            <DialogDescription>
              {selectedIds.size} recipient{selectedIds.size === 1 ? '' : 's'}. Each will get a fresh 24-hour password-set link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Template</Label>
              <Select value={bulkTemplateId} onValueChange={setBulkTemplateId}>
                <SelectTrigger data-testid="bulk-template-picker">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto — resolve per investor (recommended)</SelectItem>
                  {welcomeTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} <span className="text-xs text-gray-500 ml-2">({t.template_type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                "Auto" picks the default welcome template based on each investor's Type &amp; brand. Pick a specific template to force it for every recipient.
              </p>
            </div>
            {welcomeTemplates.length === 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                No active welcome templates found. Create one under <strong>Templates</strong> with template_type <code>welcome_email</code>, tick <em>Active</em> and <em>Default</em>, then reopen this dialog.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkInviteOpen(false)} disabled={bulkSending}>
              Cancel
            </Button>
            <Button
              onClick={submitBulkSendInvites}
              disabled={bulkSending || selectedIds.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-send-confirm"
            >
              {bulkSending ? 'Sending…' : `Send to ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite New Investor</DialogTitle>
            <DialogDescription>
              Send an email invitation to a new investor
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm(p => ({ ...p, first_name: e.target.value }))}
                  data-testid="invite-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm(p => ({ ...p, last_name: e.target.value }))}
                  data-testid="invite-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))}
                data-testid="invite-email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviting} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Investor Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-48" />
            </div>
          ) : investorDetail ? (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-xl font-bold text-slate-600">
                    {investorDetail.user?.first_name?.[0]}{investorDetail.user?.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold">
                    {investorDetail.user?.first_name} {investorDetail.user?.last_name}
                  </h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {investorDetail.user?.email}
                    </span>
                    {investorDetail.user?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {investorDetail.user?.phone}
                      </span>
                    )}
                  </div>
                </div>
                <Badge className={investorDetail.user?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {investorDetail.user?.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Portfolio Summary */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Portfolio Summary
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Total Invested</p>
                    <p className="text-lg font-bold">{formatCurrency(investorDetail.portfolio_summary?.total_invested || 0)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Current Value</p>
                    <p className="text-lg font-bold">{formatCurrency(investorDetail.portfolio_summary?.current_value || 0)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Active Loans</p>
                    <p className="text-lg font-bold">{investorDetail.portfolio_summary?.active_loans || 0}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Total Distributions</p>
                    <p className="text-lg font-bold">{formatCurrency(investorDetail.portfolio_summary?.total_distributions || 0)}</p>
                  </div>
                </div>
              </div>

              {/* KYC Documents */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  KYC Documents ({investorDetail.kyc_documents?.length || 0})
                </h4>
                {investorDetail.kyc_documents?.length > 0 ? (
                  <div className="space-y-2">
                    {investorDetail.kyc_documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{doc.document_type}</span>
                        <KycStatusBadge status={doc.status} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No KYC documents uploaded</p>
                )}
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Recent Login History
                </h4>
                {investorDetail.login_history?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {investorDetail.login_history.slice(0, 5).map((entry, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span>{formatDate(entry.created_at)}</span>
                        <span className="text-gray-500 truncate max-w-xs">{entry.user_agent}</span>
                        <Badge className={entry.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {entry.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No login history</p>
                )}
              </div>

              {/* Support Tickets */}
              <div>
                <h4 className="font-medium mb-3">Support Tickets ({investorDetail.support_tickets?.length || 0})</h4>
                {investorDetail.support_tickets?.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {investorDetail.support_tickets.slice(0, 3).map((ticket, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate max-w-xs">{ticket.subject}</span>
                        <Badge>{ticket.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No support tickets</p>
                )}
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const year = window.prompt(
                  `Generate a draft IT3(b) tax certificate for ${selectedInvestor?.first_name} ${selectedInvestor?.last_name}.\n\nEnter SA tax year (e.g. 2024 for March 2024 – February 2025):`,
                  String(new Date().getMonth() < 2 ? new Date().getFullYear() - 2 : new Date().getFullYear() - 1)
                );
                if (!year || !/^\d{4}$/.test(year.trim())) return;
                try {
                  const res = await axios.post(
                    `${API_URL}/api/admin/tax/certificates/generate`,
                    { tax_year: year.trim(), user_ids: [selectedInvestor.id] },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (res.data.generated > 0) {
                    toast.success(
                      `Draft created for tax year ${year.trim()}. Go to Tax Certificates → Draft to preview/publish.`
                    );
                  } else if (res.data.skipped > 0) {
                    toast.info(`No paid distributions found for ${selectedInvestor?.first_name} in tax year ${year.trim()}.`);
                  } else {
                    toast.error(res.data.errors?.[0]?.error || 'Nothing to generate');
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to generate certificate');
                }
              }}
              data-testid="generate-tax-cert-for-investor-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Tax Certificate
            </Button>
            <Button onClick={() => handleImpersonate(selectedInvestor)} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <LogIn className="h-4 w-4 mr-2" />
              Impersonate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Impersonation
            </DialogTitle>
            <DialogDescription>
              You are about to view the portal as {selectedInvestor?.first_name} {selectedInvestor?.last_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> This action is logged for audit purposes. 
                Only use this feature for legitimate support purposes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImpersonateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmImpersonate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open as Investor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
